import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-card/paper-card.js';
import '@polymer/iron-label/iron-label.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js';
import '@polymer/paper-listbox/paper-listbox.js';
import '@polymer/paper-item/paper-item.js';
import '@polymer/paper-button/paper-button.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';

/**
 * `cbar-payment-card` is a container element that allows for the displaying and editing (where appropriate) of payment information, currently this displays the following information:
 *  - The name of the payment
 *  - The cost of the payment
 *  - The description of the payment (TODO: Get this generating correctly depending on description source)
 *  - The next due date of the payment
 *  - The payment method used
 *  - The status of the payment
 *
 *  This element also contains a function for picking up a user editing the payment if the default values of the status and the payment type are changed (TODO: Update this to actually update the data once data structure and handling is introduced)
 *
 *  This element is also currently handling the display of the date parsed to it in Australian (dd-mm-yyyy) format and changing it to display as "dd Month, yyyy"
 *
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarPaymentCard extends PolymerElement {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: inline-flex;
            }

            .card-pushed-right {
                margin-left: 5%;
            }

            .card-end-header {
                margin-bottom: 3%;
            }

            iron-label {
                color: grey;
                font-size: 12px;
            }

            .card-header-section {
                display: flex;
                flex-direction: column;
                padding: 8px;
                color: var(--cbar-sys-color-on-secondary, var(--cbar-icon-text, white));
                background-color: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
            }

            .card-header-section * {
                color: var(--cbar-sys-color-on-secondary, var(--cbar-icon-text, white));
                background-color: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
            }

            paper-dropdown-menu {
                display: block;
                width: 75%;
            }

            .vertical-spacing {
                margin-bottom: 3%;
            }

            .delete-button {
                color: red;
            }

            .right-aligned {
                float: right;
            }
            .card-actions{
                display: flex;
                justify-content: right;
            }

            paper-card {
                width: 100%;
            }

        </style>
        <paper-card>
            <div class="card-header-section">
                <div style="display: flex; align-items: center; justify-content: space-between; white-space: nowrap;">
                    <h6 style="margin: 4px 0; overflow: hidden; text-overflow: ellipsis">[[campaign]]</h6>
                    <template is="dom-if" if="[[editing]]">
                        <span>[[amount]]</span>
                    </template>
                </div>
                <div style="overflow: hidden; text-overflow: ellipsis">[[description]]</div>
            </div>

            <div class="card-content">
                <template is="dom-if" if="[[dueDate]]">
                    <iron-label for="due-div">
                        Next Due
                    </iron-label>
                    <div id="due-div" class="vertical-spacing">[[_calculateDisplayDate(dueDate)]]</div>
                </template>
                <iron-label for="frequency-div">
                    Frequency
                </iron-label>
                <div id="frequency-div">[[frequency]]</div>

                <template is="dom-if" if="[[editing]]">
                    <paper-dropdown-menu label="Payment Method" no-animations="">
                        <paper-listbox slot="dropdown-content" on-selected-changed="_updateChange" id="method-box">
                            <template is="dom-repeat" items="[[paymentMethods]]" as="method">
                                <paper-item>[[method.name]] [[method.number]][[method.accountName]] [[method.accountNo]]
                                </paper-item>
                            </template>
                        </paper-listbox>
                    </paper-dropdown-menu>
                    <paper-dropdown-menu label="Status" no-animations="">
                        <paper-listbox slot="dropdown-content" selected="[[status]]" attr-for-selected="innerHTML" on-selected-changed="_updateChange" id="status-box">
                            <paper-item>Active</paper-item>
                            <paper-item>Cancelled</paper-item>
                        </paper-listbox>
                    </paper-dropdown-menu>
                </template>

                <template is="dom-if" if="[[!editing]]">
                    <div>
                        <iron-label for="amount-div">
                            Amount
                        </iron-label>
                        <div id="amount-div">[[amount]]</div>
                    </div>
                    <div>
                        <iron-label for="status">
                            Status
                        </iron-label>
                        <div id="status">[[status]]</div>
                    </div>

                    <template is="dom-if" if="[[startDate]]">
                        <div>
                            <iron-label for="start-date">
                                Start Date
                            </iron-label>
                            <div id="start-date">[[startDate]]</div>
                        </div>
                    </template>

                    <template is="dom-if" if="[[expiryDate]]">
                        <div>
                            <iron-label for="expire-date">
                                Expiry Date
                            </iron-label>
                            <div id="expire-date">[[expiryDate]]</div>
                        </div>
                    </template>
                </template>
            </div>

            <template is="dom-if" if="[[showActions]]">
                <div class="card-actions">
                    <paper-button class="delete-button" on-tap="_fireDelete">Delete</paper-button>
                </div>
            </template>

            <template is="dom-if" if="[[_updating]]">
                <div class="card-actions">
                    <paper-button on-tap="_updateData">Update</paper-button>
                </div>
            </template>
        </paper-card>
    `;
  }

  static get is() { return 'cbar-payment-card'; }

  static get properties() {
    return {
      /**
       * Property used for testing only, this allows for us to run tests on the element based on default property values
       */
      _name: {
        type: String,
        value: 'cbar-payment-card'
      },
      /**
       * passed in property that determines the payment that will populate the cards information
       */
      payment: {
        type: Object
      },
      campaign: {
        type: String,
        value: ""
      },
      amount: {
        type: String,
        value: ""
      },
      description: {
        type: String,
        value: ""
      },
      dueDate: {
        type: String,
        value: ""
      },
      frequency: {
        type: String,
        value: ""
      },
      paymentMethods: {
        type: Array,
        value: []
      },
      status: {
        type: String,
        value: ""
      },
      /**
       * Property that determines whether the update button will display or not
       */
      _updating: {
        type: Boolean,
        value: false
      },
      editing: {
        type: Boolean,
        value: false
      },
      showActions: {
        type: Boolean,
        value: true
      },
      startDate : {
        type: String,
        value: ""
      },
      expiryDate : {
        type: String,
        value: ""
      }
    };
  }

  /**
   * Function for reformatting the date passed into the element into a more displayable format, the array that we have to provide in this is due to the
   * default return for getMonth is an integer, whereas we wish for the display to include the months name, and not the number of the month that represents it
   *
   * @param date
   * @return {string|*}
   * @private
   */
  _calculateDisplayDate(date) {
    // Array that
    let monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    let tempDate = new Date(date.split("/").reverse().join("/"));
    date = tempDate.getDate() + " " + monthNames[tempDate.getMonth()] + ", " + tempDate.getFullYear();
    return date;
  }

  /**
   * this is called when either of the `paper-dropdown-menus` change their selected value, we need to check if these are different than the default, if so we can assume the user
   * is updating some of their information and we want to give the user a confirmation that they are updating information without giving them something obnoxious to look at
   * a simple button indicates an action based on the text within the button, so this is a simple method of being able to show the user that something is happening
   *
   * @param e
   * @private
   */
  _updateChange(e) {
    let targetedBox = e.target.id;
    let selectedValue = e.detail.value;

    if (targetedBox === "status-box") {
      if (selectedValue !== this.payment.status) {
        this._updating = true;
      } else {
        this._updating = false;
      }
    }

    if (targetedBox === "method-box") {
      /**
       * TODO: ADD DEFAULT CARD CALCULATION FUNCTION WITH EXISTING DATA STRUCTURE
       */
      this._updating = true;
    }

  }

  /**
   * Function that allows for information in the array to be updated.
   *
   * TODO: Give this actual functionality
   * @private
   */
  _updateData() {
    console.log("Update Data Function Empty");
  }

  _fireDelete(){
    this.dispatchEvent(new CustomEvent('delete-clicked', {
      bubbles: true,
      composed: true
    }));
  }
}

window.customElements.define(CbarPaymentCard.is, CbarPaymentCard);
